import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Globe } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SUPPORTED_LANGS, type AppLang, normalizeLang, chooseLanguage } from "@/i18n";
import {
  CURRENCY_META,
  SUPPORTED_CURRENCIES,
  useCurrency,
  type Currency,
} from "@/lib/currency";

const LANG_LABELS: Record<AppLang, string> = {
  es: "Español",
  en: "English",
  fr: "Français",
  pt: "Português",
  it: "Italiano",
  de: "Deutsch",
};

const FLAGS: Record<AppLang, string> = {
  es: "🇲🇽",
  en: "🇺🇸",
  fr: "🇫🇷",
  pt: "🇧🇷",
  it: "🇮🇹",
  de: "🇩🇪",
};

/**
 * Único selector compacto de idioma + moneda: `🌐 ES · MXN ▾`.
 *
 * Reutiliza exactamente la misma lógica global que los selectores existentes
 * (`setLanguage` de i18n y `useCurrency`), sin crear sistemas paralelos.
 */
export function LocalePicker({ className = "" }: { className?: string }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { currency, setCurrency } = useCurrency();
  const lang = normalizeLang(i18n.language);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, { passive: true, capture: true });
    return () => window.removeEventListener("scroll", close, { capture: true } as never);
  }, [open]);

  const changeLang = async (next: AppLang) => {
    if (next === lang) return;
    await chooseLanguage(next);
  };

  const changeCurrency = (next: Currency) => setCurrency(next);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${t("common.language", { defaultValue: "Idioma" })} / ${t("common.currency", { defaultValue: "Moneda" })}`}
          className={`inline-flex h-9 items-center gap-1.5 rounded-full border border-amber-200/25 bg-black/40 px-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-amber-100/90 backdrop-blur-md transition-all hover:border-amber-200/50 hover:bg-black/60 hover:text-amber-50 ${className}`}
        >
          <Globe className="h-3.5 w-3.5 text-amber-300" />
          <span>{lang}</span>
          <span className="text-amber-200/50">·</span>
          <span>{currency}</span>
          <ChevronDown
            className={`h-3 w-3 text-amber-200/60 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={10}
        collisionPadding={16}
        className="w-[300px] overflow-hidden rounded-2xl border border-amber-200/15 bg-[#0a0e14]/95 p-0 text-amber-50 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] backdrop-blur-xl"
      >
        <div className="grid grid-cols-2 divide-x divide-amber-200/10">
          <div>
            <div className="border-b border-amber-200/10 px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.16em] text-amber-200/70">
              {t("common.language", { defaultValue: "Idioma" })}
            </div>
            <ul role="listbox" className="p-1.5">
              {SUPPORTED_LANGS.map((lng) => {
                const active = lng === lang;
                return (
                  <li key={lng}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={active}
                      onClick={() => void changeLang(lng)}
                      className={`flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-[13px] transition-colors ${
                        active
                          ? "bg-amber-400/15 font-semibold text-amber-200"
                          : "text-amber-50/85 hover:bg-white/5 hover:text-amber-100"
                      }`}
                    >
                      <span className="text-base leading-none">{FLAGS[lng]}</span>
                      <span className="flex-1 truncate">{LANG_LABELS[lng]}</span>
                      {active && <Check className="h-3.5 w-3.5 text-amber-300" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
          <div>
            <div className="border-b border-amber-200/10 px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.16em] text-amber-200/70">
              {t("common.currency", { defaultValue: "Moneda" })}
            </div>
            <ul role="listbox" className="p-1.5">
              {SUPPORTED_CURRENCIES.map((code) => {
                const active = code === currency;
                return (
                  <li key={code}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={active}
                      onClick={() => changeCurrency(code)}
                      className={`flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-[13px] transition-colors ${
                        active
                          ? "bg-amber-400/15 font-semibold text-amber-200"
                          : "text-amber-50/85 hover:bg-white/5 hover:text-amber-100"
                      }`}
                    >
                      <span className="text-base leading-none">{CURRENCY_META[code].flag}</span>
                      <span className="flex-1 uppercase tracking-wider">{code}</span>
                      {active && <Check className="h-3.5 w-3.5 text-amber-300" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
